import React from 'react';
import { Chip } from 'radiance-ui';
import { CheckmarkIcon } from 'radiance-ui/lib/icons';

import { CtaButton, type CtaButtonProps } from 'components/global/CtaButton';
import { EfficacyStats } from 'components/global/EfficacyStats';
import { desktopOnly, mobileOnly } from 'styles/mixins/displayMobileDesktop';
import { ImportantSafetyInfoLink } from 'components/global/ImportantSafetyInfoLink';
import { StickyCta } from 'components/global/StickyCta';
import { LOCAL_COLORS } from 'styles/constants';

import {
  OuterContainer,
  InnerContainer,
  MediaContainer,
  ContentContainer,
  HeaderContainer,
  bodyStyles,
  Tags,
  TagsContainer,
  Checklist,
} from './SimpleHero.style';
import { EFFICACY_STATS } from '.';

export const SIMPLE_HERO_LAYOUT = {
  imageLast: 'Image last',
  imageFirst: 'Image first',
} as const;

export type SimpleHeroLayout = valueof<typeof SIMPLE_HERO_LAYOUT>;

export interface SimpleHeroProps {
  /** 50 characters max, preferred ~30 */
  header: JSX.Element | string;
  /** 180 characters max, preferred ~100 */
  body: React.ReactNode;
  /** Tags - optional */
  tags?: string[];
  /** Tags label - optional */
  tagsLabel?: string;
  /** Checklist - optional */
  checklist?: string[];
  /** Efficacy stats */
  efficacyStats?: boolean;
  /** Button text is required. Disclaimer text is optional */
  ctaButton: CtaButtonProps;
  /** "Important Safety Information" URL - optional */
  importantSafetyInformationUrl?: string;
  /** Must be an image */
  media: JSX.Element;
  /** Determines the arrangement of the module content */
  layout: SimpleHeroLayout;
  /** Overrides background color. Use a hex color value. Defaults to Surface Light. */
  backgroundColor?: string;
  /** Use to optionally hide the cta button on mobile */
  hideMobileCtaButton?: boolean;
}

export const SimpleHero = ({
  header,
  body,
  tags,
  tagsLabel,
  checklist,
  efficacyStats,
  ctaButton,
  importantSafetyInformationUrl,
  media,
  layout,
  backgroundColor = LOCAL_COLORS.surfaceLight,
  hideMobileCtaButton,
}: SimpleHeroProps) => {
  const moduleName = 'simpleHero';

  const formattedBody =
    typeof body === 'string' ? (
      <p css={bodyStyles}>{body}</p>
    ) : (
      <div css={bodyStyles}>{body}</div>
    );

  const bodyElement = (
    <ContentContainer hideMobileCtaButton={hideMobileCtaButton}>
      <HeaderContainer>
        {typeof header === 'string' ? (
          <strong dangerouslySetInnerHTML={{ __html: `${header}` }} />
        ) : (
          <strong>{header}</strong>
        )}
      </HeaderContainer>
      {formattedBody}
      {tags && (
        <TagsContainer>
          {tagsLabel && <h4>{tagsLabel}</h4>}
          <Tags>
            {tags.map((tag) => (
              <Chip key={tag} text={tag} status="white" />
            ))}
          </Tags>
        </TagsContainer>
      )}
      {checklist && (
        <Checklist>
          {checklist.map((item) => (
            <li key={item}>
              <CheckmarkIcon />
              {item}
            </li>
          ))}
        </Checklist>
      )}
      {efficacyStats && <EfficacyStats {...EFFICACY_STATS} />}
      <div css={desktopOnly()}>
        <CtaButton
          module={moduleName}
          buttonText={ctaButton.buttonText}
          disclaimerText={ctaButton.disclaimerText}
        />
      </div>
      {!hideMobileCtaButton && (
        <div css={mobileOnly()}>
          <StickyCta
            buttonTextOverride={ctaButton.buttonText}
            disclaimerTextOverride={`${ctaButton.disclaimerText}`}
          />
        </div>
      )}
      {importantSafetyInformationUrl && (
        <ImportantSafetyInfoLink url={importantSafetyInformationUrl} />
      )}
    </ContentContainer>
  );

  const mediaElement = <MediaContainer>{media}</MediaContainer>;

  return (
    <OuterContainer data-module={moduleName} backgroundColor={backgroundColor}>
      <InnerContainer>
        {layout === SIMPLE_HERO_LAYOUT.imageLast ? (
          <React.Fragment>
            {bodyElement}
            {mediaElement}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {mediaElement}
            {bodyElement}
          </React.Fragment>
        )}
      </InnerContainer>
    </OuterContainer>
  );
};
